import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  ElementRef,
  inject,
  DestroyRef,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Data, RouterModule, Router } from '@angular/router';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import {
  Observable,
  Subject,
  from,
  noop,
  of,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
  distinctUntilChanged,
} from 'rxjs';

import { TSIconModule, TsBadgeModule, TsButtonModule, TsOverlayModule, RedirectItem, TsTableService } from 'tsui';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { ColumnDef, DEFAULT_GRID_OPTIONS } from 'tsui/lib/ts-table/ts-table.model';
import { TsPipesModule } from 'tsui/lib/cross-cutting/pipes/ts-pipes.module';
import { Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import { GridApi, GridOptions, GridReadyEvent, SortChangedEvent, ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { FeatureFlag } from '@core/enums';
import { RelatedToType } from 'app/shared/enums';
import { Account, FieldDef, FieldDefType } from 'app/core/models';
import { AccountState } from '@state/account/state';
import { FieldDefsState } from 'app/state/fields/state';
import GraphqlService from '@services/graphql.service';
import { Dictionary } from 'app/shared/models/dictionary';
import { GraphqlConnectionMap, GraphqlFilterSort, GraphqlResult, GraphqlResultInfo } from '@shared/models/graphql';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Company } from 'app/modules/companies/companies.types';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { FormatNumberPipe } from 'app/shared/pipes/format-number.pipe';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { CompaniesService } from 'app/modules/companies/companies.service';
import { EditableModule } from '@shared/components/editable/editable.module';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ProjectTemplateService } from 'app/modules/projects/project-template.service';
import { Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { hasLinkedField } from '@shared/modules/custom-objects/custom-objects.functions';
import { TableColumn } from 'app/modules/projects/details/dashboard/panels/table/table.types';
import { getAllowedFields, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';
import { ColumnSelectorModule } from '@shared/components/column-selector/column-selector.module';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { CustomObject, CustomObjectValue } from '@shared/modules/custom-objects/custom-objects.types';
import { CustomObjectsService } from '@shared/modules/custom-objects/services/custom-objects.service';
import { RelatedTableCoreAggFooterRendererComponent } from '@shared/components/ts-table-renderers/related-table-core-agg-footer-renderer/related-table-core-agg-footer-renderer.component';

type TableType = RelatedToType.Project | RelatedToType.Property | 'CurrentProject' | string;
type TablePanelDef = PanelDef<any> & { meta: { tableType: TableType; fields: string[] } };
type SumOrAvg = 'sum' | 'avg';
type SumAndAvgs = { display: SumOrAvg; sum?: string; avg?: string; shouldUpdate: boolean };
type RelatedToTypeMapFromTableType = { [key in TableType]: string };

const MATERIAL_MODULES = [
  MatFormFieldModule,
  MatInputModule,
  MatSelectModule,
  MatMenuModule,
  MatCheckboxModule,
  MatTableModule,
  MatSortModule,
  MatIconModule,
  MatTooltipModule,
  MatButtonModule,
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatDividerModule,
];

const TSUI_MODULES = [TsButtonModule, TSIconModule, TsOverlayModule, TsBadgeModule, TsTableComponent];

@Component({
  selector: 'app-related-table-core',
  templateUrl: './related-table-core.component.html',
  styleUrls: ['./related-table-core.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DragDropModule,
    FormFieldsModule,
    DirectivesModule,
    ColumnSelectorModule,
    EditableModule,
    TsPipesModule,
    ...MATERIAL_MODULES,
    ...TSUI_MODULES,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatedTableCoreComponent implements OnInit {
  @Input() set panelDef(value: TablePanelDef) {
    this._panelDef = value;
  }

  @Input() relatedToType: RelatedToType;
  @Input() account: Account;
  @Input() project: Project;
  @Input() property: Property;
  @Input() model: CustomObjectValue;
  @Input() customObjectsOnly?: boolean;
  @Input() readonly = false;

  @ViewChildren('editableField') editableFields: QueryList<ElementRef>;

  tableType: TableType = undefined;
  initialFields: string[] = [];
  fields$: Observable<SelectableFieldDef[]>;
  customObject: CustomObject;
  columns: TableColumn[] = [];
  displayColumns: string[] = [];
  fieldDefs: FieldDef[] = [];
  changesToSave: Map<number, any> = new Map<number, any>();
  data: Data;
  isLoading = false;
  prefixColumn = '[open]';
  objectTypes = [];
  recordTypeFields = [];
  searchTxt: string;
  savingTable = false;
  canEdit = true;
  columnDefs: ColumnDef[] = [];
  newTable = signal(false);
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this._projectService.getProjectByIdBase(String(key)).pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    } as RedirectItem,
  };
  gridOptions: GridOptions;
  sortState: GraphqlFilterSort[] = [];
  items = signal<any[]>([]);

  private gridApi!: GridApi<any>;
  private _contextObject: CustomObject;
  private _panelDef: TablePanelDef;
  private _sumAndAvgs: Dictionary<SumAndAvgs> = {};
  private _items: any[] = [];
  private _previousItems: any[] = [];
  private updateTableSubject = new Subject();
  private relatedToCfExcludeList = [RelatedToType.User, RelatedToType.FileResource];
  private _relatedToTypeMapFromTableType: RelatedToTypeMapFromTableType = {
    CurrentProject: 'Project',
    Project: 'Project',
    Property: 'Property',
    Contact: 'Contact',
    Company: 'Company',
  };

  get customObjects(): CustomObject[] {
    return this.store.selectSnapshot(ObjectsManagerState.get);
  }

  // Get the context object user is currently viewing.
  get contextObject(): CustomObject {
    return this._contextObject;
  }

  // Check if the contextObject has custom object links as a relationship.
  get hasCustomObjectLinks(): boolean {
    return !!this.contextObject?.links && this.contextObject.links.length > 0;
  }

  // Check if the contextObject has record type fields.
  get hasRecordTypeFields(): boolean {
    return !!this.contextObject?.fields?.some(
      (field: FieldDef) =>
        field.type === 'record' &&
        !field.archived &&
        !this.relatedToCfExcludeList.includes(field.meta.record_type as RelatedToType),
    );
  }

  get panelDef(): TablePanelDef {
    return this._panelDef;
  }

  get isConfigured(): Signal<boolean> {
    const keys = ['tableType', 'fields'];
    return signal(
      Object.keys(this.panelDef.meta).filter((key) => keys.includes(key)).length >= keys.length,
    ).asReadonly();
  }

  get canConfirmSetup(): boolean {
    return this.tableType && this.initialFields.length > 0;
  }

  get isCurrentProjectTable(): boolean {
    return this.tableType === 'CurrentProject';
  }

  private get isProjectTable(): boolean {
    return this.tableType === RelatedToType.Project;
  }

  private get dropdownValueTypeField(): boolean {
    return this.recordTypeFields?.some((field) => field.name === this.tableType);
  }

  private get dropdownValueTypeObject(): boolean {
    return this.objectTypes?.some((obj) => obj.name === this.tableType);
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _projectService: ProjectsService,
    private readonly _propertiesService: PropertiesService,
    private readonly _companiesService: CompaniesService,
    private readonly _contactsService: ContactsService,
    private readonly store: Store,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly graphqlService: GraphqlService,
    private readonly customObjectsService: CustomObjectsService,
    private readonly projectTemplateService: ProjectTemplateService,
    private readonly tsTableService: TsTableService,
    private router: Router,
  ) {
    this.relatedToRedirectConfig = this.customObjects
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);
  }

  ngOnInit(): void {
    this.checkTable();

    this.getDropDownOptions();

    if (this.project) {
      this.canEdit = this.project.permissions?.is_editable;
    } else if (this.property) {
      this.canEdit = this.property.permissions?.is_editable;
    }

    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => (this.data = data));

    this._propertiesService.accountId = this.account.id;

    if (this.isConfigured) {
      this.tableType = this.panelDef.meta.tableType;

      this.setupConfiguredTable();

      this.updateTableSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.items.set([...this._items]);
        this._previousItems = cloneDeep(this._items);
        this._changeDetectorRef.detectChanges();
      });
    }
  }

  /**
   * Setup the configured table.
   * This is called when the table is already configured and after table setup is complete when adding the panel for the first time.
   */
  private setupConfiguredTable(): void {
    this.setupCustomObjectBasedOnValueType();
    this.setGridOptions();
    this.loadData();
  }

  private setGridOptions(): void {
    if (this.newTable()) {
      this.gridOptions = {
        ...DEFAULT_GRID_OPTIONS,
        pinnedBottomRowData: this.getPinnedRowData(),
        context: {
          sumAndAvgs: this._sumAndAvgs,
          suppressAggFuncInHeader: true,
          toggleSumOrAvg: ({ fieldDef }: TableColumn, aggregator: SumOrAvg) => {
            const column = this.columns.find((col) => col.fieldDef.name === fieldDef.name);
            if (column) this.toggleSumOrAvg(column, aggregator);
          },
          getSumOrAvg: ({ fieldDef }: TableColumn) => {
            const column = this.columns.find((col) => col.fieldDef.name === fieldDef.name);
            if (column) return this.getSumOrAvg(column);
            return null;
          },
        },
        groupIncludeFooter: (params) => {
          return params?.node?.rowIndex !== 0;
        },
        getRowStyle: (params) => {
          if (params?.node?.rowPinned) {
            return { fontWeight: 'bold', background: '#f4f4f4' };
          }
          return null;
        },
      } as GridOptions;
    }
  }

  private setupCustomObjectBasedOnValueType(): void {
    if (this.dropdownValueTypeField) {
      const selectedField = this.contextObject.fields.find((field) => field.name === this.tableType);
      this.customObject = this.customObjects.find((object) => object.name === selectedField.meta.record_type);
    }
    if (this.dropdownValueTypeObject) {
      this.customObject = this.objectTypes.find((x) => x.name === this.tableType);
    }
  }

  private getDropDownOptions(): void {
    let filteredObjects = this.customObjects.filter(
      (x) => ![RelatedToType.Project, RelatedToType.Property].includes(x.name as any),
    );

    this._contextObject = this.customObjects.find((object: CustomObject) => object.name === this.relatedToType);

    // Filter objects based on links from contextObject relationships
    if (this.hasCustomObjectLinks) {
      // Get all linked object IDs from the array of relationship objects
      filteredObjects = [
        ...this.store
          .selectSnapshot(ObjectsManagerState.get)
          .filter((x) => x.links.map((y) => y.linked_type).includes(this.contextObject.name)),
        ...this.contextObject.links
          .filter((x) => ['many_to_one', 'many_to_many'].includes(x.relationship_type))
          .map((x) => this.store.selectSnapshot(ObjectsManagerState.get).find((y) => y.name === x.linked_type)),
      ].reduce((p: any[], c: any) => {
        if (p.find((x) => x.name === c.name)) {
          return p;
        }
        return [...p, c];
      }, [] as any[]);
    }

    this.objectTypes = [
      { name: RelatedToType.Property, label: 'Properties' },
      { name: RelatedToType.Project, label: 'Child Deals' },
      { name: 'CurrentProject', label: 'Current Deal' },
      ...filteredObjects,
    ];

    this.recordTypeFields = this.hasRecordTypeFields
      ? this.contextObject.fields.filter(
          (field: FieldDef) =>
            field.type === 'record' &&
            !field.archived &&
            !this.relatedToCfExcludeList.includes(field.meta.record_type as RelatedToType),
        )
      : [];
  }

  private getPinnedRowData(): { [key: string]: string }[] {
    if (!this.columns || this.columns.length === 0) return [];

    return [
      this.columns.reduce((acc, col) => {
        acc[col.name] = this.getSumOrAvg(col);
        return acc;
      }, {}),
    ];
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.setPinnedRowData();
  }

  private setPinnedRowData(): void {
    if (this.gridApi && this.newTable()) {
      this.gridApi.setGridOption('pinnedBottomRowData', this.getPinnedRowData());
    }
  }

  // Hide custom object if related linked field is archived.
  hideObjectTypeBasedOnArchivedLinkedField(object: CustomObject): boolean {
    return object?.fields?.some((field) => hasLinkedField(field) && field.archived);
  }

  goToRecordBasedOnType(tableType: TableType, row: any): void {
    const customFieldBelongsTo = this.contextObject.fields.find((field) => field.name === tableType)?.meta.record_type;

    if (row.custom_object_id) {
      this.router.navigate(['/objects', row.custom_object_id, row.id, 'details']);
    }

    if (customFieldBelongsTo === RelatedToType.Property) {
      this.router.navigate(['/properties', row.id, 'details']);
    }

    if (customFieldBelongsTo === RelatedToType.Project) {
      this.router.navigate(['/projects', row.pipeline_id, row.id, 'details']);
    }

    if (customFieldBelongsTo === RelatedToType.Company) {
      this.router.navigate(['/companies', row.id]);
    }

    if (customFieldBelongsTo === RelatedToType.Contact) {
      this.router.navigate(['/contacts', row.id]);
    }
  }

  onSaveColumns(selectableFieldDefs: SelectableFieldDef[]): void {
    this.columns = [
      ...this.columns.filter((x) => selectableFieldDefs.map((y) => y.name).includes(x.name)),
      ...selectableFieldDefs
        .filter((x) => !this.columns.map((y) => y.name).includes(x.name))
        .map((x) => this.mapToTableColumn(x)),
    ] as TableColumn[];

    this.displayColumns = [
      this.prefixColumn,
      ...this.columns.map(({ name, fieldDef: { meta } }) => this._getFieldName(name, meta)),
    ];

    if (this.newTable() && this.gridApi) {
      // Update the column definitions
      this.getColDefs();
      // Update the grid options with new column definitions
      this.gridOptions.columnDefs = this.columnDefs as unknown as (ColDef<any, any> | ColGroupDef<any>)[];
      // Force the grid to refresh
      this.gridApi.refreshCells({ force: true });
    }

    this._updateTemplate();
  }

  confirmTableType(): void {
    const meta = {
      fields: this.initialFields,
      tableType: this.tableType,
    };

    this.getTemplateObservable()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((projectTemplate) => {
          if (this.relatedToType?.startsWith('co_')) {
            return this.handleTemplateForCustomObject(projectTemplate, meta);
          }

          const { panelIndex, laneIndex, tabIndex } = this._getLaneAndPanelIndexes(
            projectTemplate.definition.dashboard,
          );
          const template: ProjectTemplate<any> = cloneDeep(projectTemplate);

          if (tabIndex > -1) {
            template.definition.dashboard.tabs[tabIndex].panels[panelIndex] = {
              ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex],
              meta,
            };
          } else {
            template.definition.dashboard.panels[laneIndex][panelIndex] = {
              ...template.definition.dashboard.panels[laneIndex][panelIndex],
              meta,
            };
          }

          return this._projectService.updateProjectTemplate(template);
        }),
        tap(() => {
          this._panelDef.meta = meta;
          this.setupConfiguredTable();
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  hasChangesToSave(): boolean {
    return !!Array.from(this.changesToSave.values()).length;
  }

  discardChanges(): void {
    this._items = cloneDeep([...this._previousItems]);
    this.updateTableSubject.next(true);
    this.changesToSave.clear();
    this.loadData();
    this._changeDetectorRef.detectChanges();
  }

  onSave(): void {
    this.savingTable = true;
    const requests = [];
    const MAX_PARALLEL_REQUESTS = 6;

    for (let id of this.changesToSave.keys()) {
      requests.push(this.createSaveObservable({ id, ...this.changesToSave.get(id) }));
    }

    from(requests)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap((observable) => observable, MAX_PARALLEL_REQUESTS),
        finalize(() => {
          this.changesToSave.clear();
          this.savingTable = false;
          this._previousItems = cloneDeep(this._items);
          this.updateSumAndAvgs();
          this.setPinnedRowData();
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  createSaveObservable(data: any): Observable<any> {
    let resultObservable: Observable<unknown>;

    if (!this._relatedToTypeMapFromTableType[this.tableType] && this.dropdownValueTypeObject) {
      return this.customObjectsService.updateValue(Number(this.account.id), this.customObject.id, data.id, {
        ...data,
        data,
      });
    } else if (this.dropdownValueTypeField) {
      const customFieldBelongsTo = this.contextObject.fields.find((field) => field.name === this.tableType)?.meta
        .record_type;

      switch (customFieldBelongsTo) {
        case RelatedToType.Property:
          resultObservable = this._propertiesService.savePropertyDirect(data as Property);
          break;
        case RelatedToType.Project:
          resultObservable = this._projectService.updateProject(data, true);
          break;
        case RelatedToType.Company:
          resultObservable = this._companiesService.updateCompany(data as Company);
          break;
        case RelatedToType.Contact:
          resultObservable = this._contactsService.updateContact(data as Contact);
          break;
        default:
          resultObservable = this.customObjectsService.updateValue(
            Number(this.account.id),
            this.customObject.id,
            data.id,
            {
              ...data,
              data,
            },
          );
          break;
      }
    } else {
      if (this.isProjectTable || this.isCurrentProjectTable) {
        resultObservable = this._projectService.updateProject(data, true);
      } else {
        switch (this.tableType) {
          case RelatedToType.Property:
            resultObservable = this._propertiesService.savePropertyDirect(data as Property);
            break;
          case RelatedToType.Company:
            resultObservable = this._companiesService.updateCompany(data as Company);
            break;
          case RelatedToType.Contact:
            resultObservable = this._contactsService.updateContact(data as Contact);
            break;
        }
      }
    }

    return resultObservable.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((response) => {
        const responseId = Number(response?.id ?? response[this.tableType.toLowerCase()]?.id);
        const recordIndex = this._items.findIndex(({ id }) => responseId === Number(id));
        const updatedData = response[this.tableType.toLowerCase()] ?? response;
        this._items[recordIndex] = { ...this._items[recordIndex], ...updatedData };
        this.updateSumAndAvgs();
        this._changeDetectorRef.detectChanges();
      }),
    );
  }

  onFieldUpdate(event: Dictionary<any>, entity: Project | Property, fieldName: string): void {
    const eventReceived = this.newTable() ? { [event.colDef.field]: event.newValue } : event;
    const model = {
      ...(this.changesToSave.get(+entity.id) || {}),
      ...eventReceived,
    };
    this.changesToSave.set(+entity.id, model);

    this.updateTableWhenRecordEdited(event, +entity.id, fieldName);
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const columnsStg = this.displayColumns.filter((x) => x !== this.prefixColumn);
      moveItemInArray(columnsStg, event.previousIndex, event.currentIndex);
      moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
      this.displayColumns = [this.prefixColumn, ...columnsStg];
      this._updateTemplate();
    }
  }

  getSumOrAvg({ fieldDef }: TableColumn): string {
    const fieldName = this._getFieldName(fieldDef.name, fieldDef.meta);
    if (!['decimal', 'number', 'money'].includes(fieldDef.type)) return null;
    if (fieldName === 'id') return null;

    let fieldSumAndAvgs: SumAndAvgs;
    if ((fieldSumAndAvgs = this._sumAndAvgs[fieldName]) && !fieldSumAndAvgs.shouldUpdate) {
      return this._formatSumAvgValue(fieldSumAndAvgs);
    }

    const value = this._items.reduce((prev: number, cur: any) => (Number(cur[fieldName]) || 0) + prev, 0);

    if (value === 0) return null;

    this._sumAndAvgs[fieldName] = {
      display: fieldSumAndAvgs?.display || 'sum',
      sum: this._formatNumericValue(fieldDef, value),
      avg: this._formatNumericValue(fieldDef, String(value / this._items.length)),
      shouldUpdate: false,
    };

    return this._formatSumAvgValue(this._sumAndAvgs[fieldName]);
  }

  toggleSumOrAvg({ fieldDef }: TableColumn, aggregator: SumOrAvg): void {
    const fieldName = this._getFieldName(fieldDef.name, fieldDef.meta);
    this._sumAndAvgs[fieldName].display = aggregator;
    this.setPinnedRowData();
  }

  filterFn = (values: CustomObject[]): any[] => {
    const result = values;

    if (this.searchTxt) {
      return result.filter((x) => x.label.toLowerCase().includes(this.searchTxt.toLowerCase()));
    }

    return result;
  };

  filterFieldsFn = (fields: FieldDef[]): FieldDef[] => {
    if (this.searchTxt) {
      return fields.filter((x) => x.label.toLowerCase().includes(this.searchTxt.toLowerCase()));
    }

    return fields;
  };

  onTableTypeChanged(): void {
    this.initialFields = [];
    this._loadFields(this.tableType, false);
  }

  onTableTypeOpenedChanged(event: boolean): void {
    if (event === false) {
      this.searchTxt = '';
    }
  }

  private updateSumAndAvgs(): void {
    this._sumAndAvgs = Object.keys(this._sumAndAvgs).reduce((prev: Dictionary<SumAndAvgs>, cur: string) => {
      prev[cur] = { display: this._sumAndAvgs[cur].display, shouldUpdate: true };
      return prev;
    }, {} as Dictionary<SumAndAvgs>);
  }

  private updateTableWhenRecordEdited(event: Dictionary<any>, entityId: number, fieldName: string): void {
    if (this.fieldDefs.find((x) => x.name === fieldName)?.type !== 'record') {
      return;
    }

    const entityIndex = this._items.findIndex(({ id }) => +id === entityId);
    let entity;

    if ((entity = this._items[entityIndex])) {
      this._items[entityIndex] = { ...entity, [fieldName]: event[fieldName] };
    }

    this.items.set([...this._items]);
  }

  private getTemplateObservable(): Observable<ProjectTemplate<any>> {
    if (this.property) {
      return this._projectService.getProjectTemplateByRelatedToId(Number(this.property.id), RelatedToType.Property);
    } else if (this.project) {
      return this._projectService.getProjectTemplateByRelatedToId(Number(this.project.id), RelatedToType.Project);
    } else if (this.model) {
      return this._projectService.getProjectTemplateByIdBase(this.model.template_id, Number(this.account.id));
    }
  }

  private _formatSumAvgValue({ display, sum, avg }: SumAndAvgs): string {
    return `${display}: ${display === 'sum' ? sum : avg}`;
  }

  private _formatNumericValue(fieldDef: FieldDef, value: string): string {
    switch (fieldDef.type) {
      case 'number':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 0);
      case 'decimal':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 2);
      case 'money':
        // eslint-disable-next-line no-case-declarations
        const precision = fieldDef.meta?.scale || 0;
        if (value) {
          return new CurrencyPipe('en-US').transform(value, 'USD', 'symbol', '1.' + precision + '-' + precision);
        } else {
          return null;
        }
      default:
        return null;
    }
  }

  private _updateTemplate(): void {
    let meta;

    this.getTemplateObservable()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((projectTemplate) => {
          if (this.relatedToType?.startsWith('co_')) {
            return this.handleTemplateForCustomObject(projectTemplate);
          }

          const { panelIndex, laneIndex, tabIndex } = this._getLaneAndPanelIndexes(
            this.project ? this.project.template.definition.dashboard : this.property.template.definition.dashboard,
          );
          const template: ProjectTemplate<any> = cloneDeep(projectTemplate);

          if (tabIndex > -1) {
            meta = {
              ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex].meta,
              fields: this.displayColumns.filter((x) => x !== this.prefixColumn),
            };

            template.definition.dashboard.tabs[tabIndex].panels[panelIndex] = {
              ...template.definition.dashboard.tabs[tabIndex].panels[panelIndex],
              meta,
            };
          } else {
            meta = {
              ...template.definition.dashboard.panels[laneIndex][panelIndex].meta,
              fields: this.displayColumns.filter((x) => x !== this.prefixColumn),
            };

            template.definition.dashboard.panels[laneIndex][panelIndex] = {
              ...template.definition.dashboard.panels[laneIndex][panelIndex],
              meta,
            };
          }

          return this._projectService.updateProjectTemplate(template, false).pipe(
            tap(() => {
              this._panelDef.meta = meta;
            }),
          );
        }),
      )
      .subscribe(noop);
  }

  private handleTemplateForCustomObject(
    projectTemplate: ProjectTemplate<any>,
    meta?: any,
  ): Observable<ProjectTemplate<any>> {
    const { panelIndex, tabIndex } = this.getTabAndPanelIndexes(projectTemplate.definition);
    const template: ProjectTemplate<any> = cloneDeep(projectTemplate);

    if (!meta) {
      meta = {
        ...template.definition.tabs[tabIndex].layout[panelIndex].panel.meta,
        fields: this.displayColumns.filter((x) => x !== this.prefixColumn),
      };
    }

    template.definition.tabs[tabIndex].layout[panelIndex].panel = {
      ...template.definition.tabs[tabIndex].layout[panelIndex].panel,
      meta,
    };

    return this.projectTemplateService.update(template).pipe(
      tap(() => {
        this._panelDef.meta = meta;
      }),
    );
  }

  private getTabAndPanelIndexes(definition: any): {
    panelIndex: number;
    tabIndex: number;
  } {
    let tabIndex, panelIndex;

    for (const [i, tab] of Object.entries(definition.tabs)) {
      panelIndex = (tab as any).layout.findIndex((layout) => layout.panel.id === this.panelDef.id);

      if (panelIndex !== -1) {
        tabIndex = i;
        break;
      }
    }

    return { tabIndex, panelIndex };
  }

  private _getLaneAndPanelIndexes(dashboard: { panels: any[]; tabs: any[] }): {
    laneIndex: number;
    panelIndex: number;
    tabIndex: number;
  } {
    const { panels, tabs } = dashboard;

    let panelIndex = -1;

    const laneIndex = panels.findIndex((panels) => {
      panelIndex = panels.findIndex((panel) => panel.id === this.panelDef.id);
      return panelIndex > -1;
    });

    let tabIndex = -1;

    if (laneIndex < 0) {
      tabIndex = tabs.findIndex((tab) => {
        panelIndex = tab.panels.findIndex((panel) => panel.id === this.panelDef.id);
        return panelIndex > -1;
      });
    }

    return { panelIndex, laneIndex, tabIndex };
  }

  /**
   * Loads and processes fields based on the table type and dropdown value type.
   * When dropdown type is 'field', it gets fields related to the record_type of the selected field.
   * When dropdown type is 'object', it gets fields related to the selected object type.
   *
   * @param tableType - The type of table being configured (e.g., Project, Property, or Custom Object)
   * @param setSelected - Whether to set the selected panel fields from panelDef.meta.fields.
   * @returns Observable of SelectableFieldDef[] - The processed fields with their selection state
   * @sets this.fields$ - Assigns the observable to the component's fields$ property
   */
  private _loadFields(tableType: TableType, setSelected: boolean): Observable<SelectableFieldDef[]> {
    const relatedTo = this._relatedToTypeMapFromTableType[tableType] ?? tableType;
    const fieldsSnapshot$ = of(this.store.selectSnapshot(FieldDefsState.getFieldDefs));

    const fields$ = fieldsSnapshot$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((fields) => {
        if (this.dropdownValueTypeField) return this.setFieldsForFieldValues(relatedTo, fields, setSelected);

        if (this.dropdownValueTypeObject) return this.setFieldsForObjectValues(relatedTo, fields, setSelected);
      }),
    );

    this.fields$ = fields$;

    return fields$;
  }

  /**
   * Basic and commonly used filtering and mapping of the fields for the dropdown value type.
   * @param fields - The fields.
   * @param criteria - The filter criteria.
   * @param setSelected - Whether to set the selected property.
   * @returns The selectable field defs.
   */
  private processFieldsForDropdownValueType(
    fields: FieldDef[],
    criteria: string,
    setSelected: boolean,
  ): SelectableFieldDef[] {
    return fields
      .filter((field) => field.related_to_type === criteria && !field.archived)
      .map((field) => ({
        ...field,
        selected: setSelected
          ? this.panelDef.meta?.fields.filter(
              (columnName: string) => columnName === this._getFieldName(field.name, field.meta),
            )?.length > 0
          : false,
      }));
  }

  /**
   * Called from the `loadData` method when the dropdown value type is 'object'.
   * @param relatedTo - The related to type from the panel definition.
   * @param fields - The fields.
   * @returns The selectable field defs.
   */
  private setFieldsForObjectValues(relatedTo: string, fields: FieldDef[], setSelected: boolean): SelectableFieldDef[] {
    this.fieldDefs = getAllowedFields(
      fields,
      relatedTo === 'CurrentProject' ? RelatedToType.Project : (relatedTo as RelatedToType),
    );

    return this.processFieldsForDropdownValueType(fields, relatedTo, setSelected);
  }

  /**
   * Called from the `loadData` method when the dropdown value type is 'field'.
   * @param relatedTo - The related to type from the panel definition.
   * @param fields - The fields.
   * @returns The selectable field defs.
   */
  private setFieldsForFieldValues(relatedTo: string, fields: FieldDef[], setSelected: boolean): SelectableFieldDef[] {
    // Find the selected field to get its record_type
    const selectedField = fields.find((field) => field.name === relatedTo);

    if (!selectedField?.meta?.record_type) {
      return [];
    }

    this.fieldDefs = getAllowedFields(fields, selectedField.meta.record_type as RelatedToType);

    return this.processFieldsForDropdownValueType(fields, selectedField.meta.record_type, setSelected);
  }

  private setColumnsOnLoadData(): void {
    this.columns = removeArchivedFieldsFromTableDefinition(this.panelDef.meta.fields, this.fieldDefs)
      .filter((col) => col != null)
      .map((fieldName) => {
        const selectableFieldDef = this.lookupField(fieldName);
        if (selectableFieldDef) {
          const { label, name, type, meta } = selectableFieldDef;
          return {
            name: this._getFieldName(name, meta),
            label,
            type,
            isCustom: meta?.is_custom ?? false,
            fieldDef: selectableFieldDef,
          } as TableColumn;
        }
      });

    this.displayColumns = [this.prefixColumn, ...this.columns.map(({ name }) => name)];
  }

  private setProjectTableType(): void {
    if (this.project?.children) {
      this._items = this.project.children;
      this._previousItems = cloneDeep(this._items);
      this.items.set(this._items);
    }
  }

  private setCurrentProjectTableType(): void {
    if (this.project) {
      this._items = [this.project];
      this._previousItems = cloneDeep(this._items);
      this.items.set(this._items);
    }
  }

  private setPropertiesTableType(): void {
    if (this.project?.properties) {
      this._propertiesService
        .getProperties(
          this.displayColumns.filter((x) => x !== this.prefixColumn),
          {
            fjson: JSON.stringify([['id', 'in', this.project.properties.map((prop) => prop.id)]]),
          },
          0,
          100,
          this.sortState[0]?.field || 'name',
          (this.sortState[0]?.order as 'asc' | 'desc' | '') || 'asc',
        )
        .pipe(
          tap((response) => {
            this._items = response.properties;
            this._previousItems = cloneDeep(this._items);
            this.items.set(this._items);
          }),
          map((response) => response.properties),
        )
        .subscribe();
    }
  }

  private setDefaultTableType(): void {
    let fjson = null;
    let connection = this.tableType;

    let fieldsForGraphQL = ['id', 'created_at', 'updated_at', 'template_id', ...this.panelDef.meta.fields];

    // If the dropdown value type is a field, get the connection from the selected field.
    if (this.dropdownValueTypeField) {
      const selectedField = this.store
        .selectSnapshot(FieldDefsState.getFieldDefs)
        .find((field) => field.name === this.tableType);

      // Get the connection from the selectedField record_type value.
      connection = selectedField?.meta?.record_type as string;

      const modelValue =
        this.model?.[this.tableType] ?? this.project?.[this.tableType] ?? this.property?.[this.tableType];

      // Handle both single and multi-select values.
      const linkedIds = Array.isArray(modelValue)
        ? modelValue
            ?.filter((item) => item) // Filter out undefined/null values
            .map((item) => item.split('|')[0])
        : modelValue
          ? [modelValue.split('|')[0]]
          : [];

      if (linkedIds.length > 0) {
        fjson = JSON.stringify([['id', 'in', linkedIds]]);
      } else {
        // If no linkedIds, set items$ to empty array and return early
        this.clearItemsWhenNoLinkedField();
        return;
      }
    }
    // If the dropdown value type is an object, get the connection from...
    if (this.dropdownValueTypeObject) {
      const setFjsonProperty = (relatedToType: RelatedToType): string => {
        const field =
          this.store
            .selectSnapshot(ObjectsManagerState.get)
            ?.find((x) => x.name === this.tableType)
            ?.links.find((x) => x.linked_type === this.contextObject.name)?.custom_field ??
          this.store
            .selectSnapshot(ObjectsManagerState.get)
            .find((x) => x.name === this.relatedToType)
            .links.find((x) => x.linked_type === this.customObject.name).custom_field;

        if (field) {
          return this.nestedFiltersEnabled ? `${field.name}.id` : field.name;
        }

        return null;
      };

      if (this.project) {
        const projectField = setFjsonProperty(RelatedToType.Project);
        if (!projectField) {
          this.clearItemsWhenNoLinkedField();
          return;
        }
        fjson = JSON.stringify([
          [projectField, 'in', this.nestedFiltersEnabled ? [this.project.id] : [[this.project.id]]],
        ]);
      } else if (this.property) {
        const propertyField = setFjsonProperty(RelatedToType.Property);
        if (!propertyField) {
          this.clearItemsWhenNoLinkedField();
          return;
        }
        fjson = JSON.stringify([
          [propertyField, 'in', this.nestedFiltersEnabled ? [this.property.id] : [[this.property.id]]],
        ]);
      } else if (this.model) {
        const modelField = setFjsonProperty(this.relatedToType);
        if (!modelField) {
          this.clearItemsWhenNoLinkedField();
          return;
        }
        fjson = JSON.stringify([[modelField, 'in', this.nestedFiltersEnabled ? [this.model.id] : [[this.model.id]]]]);
      }
    }

    //eslint-disable-next-line no-case-declarations
    const linkableObjects: RelatedToType[] = this.customObjects
      .filter((object) => object.linkable && !object.name.startsWith('co_'))
      .map((object) => object.name as RelatedToType);

    //eslint-disable-next-line no-case-declarations
    const isConnectionStandardObjectType = linkableObjects.includes(connection as RelatedToType);
    const isConnectionProjectObjectType = connection === RelatedToType.Project;

    if (!isConnectionStandardObjectType) {
      fieldsForGraphQL.push('related_to_id', 'custom_object_id');
    }

    if (isConnectionProjectObjectType) {
      fieldsForGraphQL.push('pipeline_id');
    }

    this.loadGraphQLData(connection, fieldsForGraphQL, fjson, this.sortState, isConnectionStandardObjectType);
  }

  private clearItemsWhenNoLinkedField(): void {
    this._items = [];
    this._previousItems = [];
    this.items.set([]);
  }

  private loadGraphQLData(
    connection: string,
    fieldsForGraphQL: string[],
    fjson: string,
    sort: GraphqlFilterSort[],
    isConnectionStandardObjectType: boolean,
  ): void {
    this.graphqlService
      .query(
        {
          connection,
          fieldDefs: [],
          fields: fieldsForGraphQL,
          filter: { fjson, sort },
          processNodeFields: !isConnectionStandardObjectType,
        },
        null,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        map(({ edges }) => edges.map(({ node }) => node)),
        tap((response) => {
          this._items = response;
          this._previousItems = cloneDeep(this._items);
          this.items.set(this._items);
        }),
      )
      .subscribe();
  }

  private loadData(): void {
    this.fields$ = this._loadFields(this.tableType, true).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        this.setColumnsOnLoadData();

        this.getColDefs();

        switch (this.tableType) {
          // Case selected object from dropdown equals to 'Child Deals'.
          case RelatedToType.Project:
            this.setProjectTableType();
            break;
          // Case selected object from dropdown equals to 'Current Deal'.
          case 'CurrentProject':
            this.setCurrentProjectTableType();
            break;
          // Case selected object from dropdown equals to 'Properties'.
          case RelatedToType.Property:
            this.setPropertiesTableType();
            break;
          case RelatedToType.Company:
          case RelatedToType.Contact:
            this.setStandardTableType(this.tableType);
            break;
          // Default case (Custom Objects and record_type related fields).
          default:
            this.setDefaultTableType();
            break;
        }
      }),
    );
  }

  private get nestedFiltersEnabled(): boolean {
    return this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));
  }

  private setStandardTableType(relatedTo: RelatedToType): void {
    const linkedField = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .find((x) => x.name === this.tableType)
      .links.find((x) => x.linked_type === this.contextObject.name).custom_field;
    const id = (this.project || this.property || this.model)?.id;
    const fjson = this.nestedFiltersEnabled
      ? [[`${linkedField.name}.id`, 'in', [id]]]
      : [[`${linkedField.name}`, 'in', [[id]]]];

    this.graphqlService
      .query({
        connection: GraphqlConnectionMap[relatedTo],
        fields: this.displayColumns.filter((x) => x !== this.prefixColumn),
        filter: {
          fjson: JSON.stringify(fjson),
        },
        options: {
          keepConnectionCase: true,
        },
      })
      .pipe(
        tap((response: GraphqlResultInfo<any>) => {
          this._items = response.edges.map((x) => x.node);
          this._previousItems = cloneDeep(this._items);
          this.items.set(this._items);
        }),
      )
      .subscribe(noop);
  }

  private getColDefs(): void {
    if (this.newTable()) {
      this.displayColumns = removeArchivedFieldsFromTableDefinition(this.displayColumns, this.fieldDefs);
      this.columnDefs = this.displayColumns
        .filter((column) => column !== this.prefixColumn)
        .map((column) => this.lookupField(column));

      this.setColumnsAttributes();
    }
  }

  private setColumnsAttributes(): void {
    this.columnDefs = this.columns.map((column: TableColumn) => ({
      label: column.label,
      name: column.name,
      type: (params) => {
        return params?.node?.rowPinned ? ('text' as FieldDefType) : column.fieldDef.type;
      },
      tsTableOptions: {
        sortable: true,
        flex: 1,
        minWidth: 100,
        wrapText: true,
        filter: true,
        resizable: true,
        editable: (params) => {
          return (
            !params?.node?.rowPinned &&
            !column.fieldDef.meta?.readonly &&
            !column.fieldDef.locked &&
            !column.fieldDef.meta?.value_hidden &&
            column.name !== 'id'
          );
        },
        cellRendererSelector: (params) => {
          if (params?.node?.rowPinned) {
            return {
              component: RelatedTableCoreAggFooterRendererComponent,
            };
          } else {
            return this.tsTableService.setCellRendererSelector(
              column.fieldDef,
              this.relatedToRedirectConfig,
              this.data,
            );
          }
        },
        cellEditorSelector: () => {
          if (column.fieldDef.meta?.record_type === RelatedToType.User) {
            column.fieldDef = {
              meta: {
                options: this.store
                  //@ts-ignore
                  .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
                  .map((x) => ({ id: x.id, label: x.name })),
              },
              ...column.fieldDef,
            };
          }
          return this.tsTableService.setCellEditorSelector(column.fieldDef, this.relatedToRedirectConfig, this.data);
        },
        valueFormatter: (params) => {
          return this.tsTableService.setValueFormatterSelector(
            column.fieldDef,
            this.relatedToRedirectConfig,
            this.data,
          )(params);
        },
      },
    }));
  }

  private checkTable(): void {
    this.newTable.set(this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE)));
  }

  private lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  handleOpenDetails(data: any): void {
    switch (this.tableType) {
      case RelatedToType.Property:
        this.router.navigate(['/properties', data.id]);
        break;

      case RelatedToType.Project:
        this.router.navigate(['/projects', data.pipeline_id, data.id]);
        break;

      default:
        this.goToRecordBasedOnType(this.tableType, data);
        break;
    }
  }

  onColumnMoved(event: any): void {
    //SC-45024 - The displayColumns array is 1-indexed with the prefix column, but the event.toIndex is 0-indexed.
    event.toIndex = event.toIndex - 1;
    TsTableService.moveColumns(event, this.displayColumns, [], (columns: string[]) => {
      this._updateTemplate();
    });
  }

  onCellEditingStopped(event): void {
    this.onFieldUpdate(event, event.data, event.colDef.field);
  }

  onSortChanged(event: SortChangedEvent): void {
    const sortedColumn = event.columns.find((col) => col.getSort());

    if (sortedColumn?.getColDef()?.field && sortedColumn?.getSort()) {
      this.sortState = [
        {
          field: sortedColumn.getColDef().field,
          order: sortedColumn.getSort() as 'asc' | 'desc',
        },
      ];
      this.loadData();
    }
  }

  onMatSortChanged(event: MatSort): void {
    this.sortState = [
      {
        field: event.active,
        order: event.direction as 'asc' | 'desc',
      },
    ];
    this.loadData();
  }

  private _getFieldName(name: string, meta: any): string {
    const fieldDef = this.lookupField(name);
    if (fieldDef) {
      return fieldDef?.name;
    } else {
      return `${meta?.is_custom ? 'cf_' : ''}${name}`;
    }
  }

  private mapToTableColumn(field: SelectableFieldDef): TableColumn {
    const { name, label, type, meta } = field;

    return {
      label,
      type,
      name: this._getFieldName(name, meta),
      isCustom: meta?.is_custom ?? false,
      fieldDef: field,
    } as TableColumn;
  }
}
